import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { type UserCreateFormData, type UserEditFormData } from '@/lib/validations/user'
import { userService, workspaceService, projectService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import type { Workspace, Project } from '@/types'
import { UserRole } from '@/types/enums'
import { PageContainer, PageHeader } from '@/components/layout'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { UserForm } from '@/components/users/UserForm'

export default function UserCreatePage() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [allProjects, setAllProjects] = useState<Project[]>([])
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null)
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([])
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  // Load workspaces
  const loadWorkspaces = useCallback(async () => {
    if (!currentUser) return

    try {
      const allWorkspaces = await workspaceService.getAll()

      // SuperAdmins see all workspaces, others see their own
      if (currentUser.role === UserRole.SuperAdmin) {
        setWorkspaces(allWorkspaces)
      } else {
        const userWorkspaces = allWorkspaces.filter(
          (workspace) =>
            workspace.creator.id === currentUser.id ||
            workspace.users.some((u) => u.id === currentUser.id)
        )
        setWorkspaces(userWorkspaces)
      }
    } catch (error) {
      console.error('Failed to load workspaces:', error)
      toast.error('Failed to load workspaces')
    }
  }, [currentUser])

  // Load projects when workspace changes
  const loadProjects = useCallback(async () => {
    if (!selectedWorkspace) {
      setAllProjects([])
      setSelectedProjects([])
      return
    }

    try {
      const projects = await projectService.getAll()

      // Filter projects that belong to selected workspace
      const relevantProjects = projects.filter((p) => p.workspace.id === selectedWorkspace.id)
      setAllProjects(relevantProjects)

      // Remove any selected projects that are no longer available
      setSelectedProjects((prev) =>
        prev.filter((p) => relevantProjects.some((rp) => rp.id === p.id))
      )
    } catch (error) {
      console.error('Failed to load projects:', error)
      toast.error('Failed to load projects')
    }
  }, [selectedWorkspace])

  useEffect(() => {
    loadWorkspaces()
  }, [loadWorkspaces])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  const onSubmit = async (data: UserCreateFormData | UserEditFormData) => {
    if (!currentUser) return

    // Only SuperAdmins can create users
    if (currentUser.role !== UserRole.SuperAdmin) {
      toast.error('Only SuperAdmins can create new users')
      return
    }

    setLoading(true)
    try {
      // Create the new user
      const newUser = await userService.create({
        name: data.name,
        email: data.email,
        password: 'password' in data ? data.password : '',
        location: data.location,
        role: data.role,
        timezone: data.timezone,
        status: data.status,
        workspaces: selectedWorkspace ? [selectedWorkspace] : [],
        projects: selectedProjects,
        tasks: [],
        messages: [],
        documents: [],
      })

      // Update workspace to include the new user
      if (selectedWorkspace) {
        const existingUsers = selectedWorkspace.users.filter((u) => u.id !== newUser.id)
        await workspaceService.update(selectedWorkspace.id, {
          users: [...existingUsers, newUser],
        } as Partial<Workspace>)
      }

      // Update projects to include the new user
      for (const project of selectedProjects) {
        const existingUsers = project.users.filter((u) => u.id !== newUser.id)
        await projectService.update(project.id, {
          users: [...existingUsers, newUser],
        } as Partial<Project>)
      }

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('User created successfully')
      navigate(`/users/${newUser.id}`)
    } catch (error) {
      console.error('Failed to create user:', error)
      toast.error('Failed to create user')
    } finally {
      setLoading(false)
    }
  }

  // Check if current user has permission
  if (currentUser?.role !== UserRole.SuperAdmin) {
    return (
      <PageContainer maxWidth="full">
        <PageHeader
          title="Access Denied"
          description="Only SuperAdmins can create new users"
          backButton={{
            label: 'Back to Users',
            onClick: () => navigate('/users'),
          }}
        />
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title="Create New User" />

      <UserForm
        mode="create"
        workspaces={workspaces}
        allProjects={allProjects}
        selectedWorkspace={selectedWorkspace}
        selectedProjects={selectedProjects}
        onWorkspaceChange={setSelectedWorkspace}
        onProjectsChange={setSelectedProjects}
        onSubmit={onSubmit}
        onCancel={() => navigate('/users')}
        loading={loading}
        onDirtyChange={setIsDirty}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
